// src/pages/ListYourShowPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../config/api";
import { get, del } from "../lib/http";

const formatDDMonYYYY = (value) => {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return "–";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}/${mon}/${yyyy}`;
};
const cn = (...a) => a.filter(Boolean).join(" ");
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function ListYourShowPage() {
  const nav = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  // UI state
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [city, setCity] = useState("all");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("latest");
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });

  const user = JSON.parse(localStorage.getItem("bb_user"));

  /* ---- LOAD with safe fallbacks ---- */
  const load = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      let list = [];
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token || null;
      if (token) {
        try {
          const res = await get(`${API.events.getEvent}?createdBy=${user._id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          if (Array.isArray(res?.events)) {
            const byId = new Map();
            [...list, ...res.events].forEach((ev) => {
              const key = ev?._id || ev?.id;
              if (key) byId.set(key, ev);
            });
            list = Array.from(byId.values());
            setRows(list);
          } else {
            if (res && !Array.isArray(res?.events)) {
              setErrorMsg("Protected endpoint returned unexpected shape; showing partial list.");
            }
          }
        } catch (err) {
          console.warn("Secure fetch failed; showing partial list.", err);
          setErrorMsg("Could not load full list (auth). Showing partial results.");
        }
      }
    } catch (e) {
      console.error("Events fetch error:", e);
      setRows([]);
      setErrorMsg(e?.message || "Failed to load events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  /* ---- Derived options ---- */
  const options = useMemo(() => {
    const cities = new Set();
    const cats = new Set();
    rows.forEach((r) => {
      if (r?.venue?.city) cities.add(String(r.venue.city));
      if (r?.category) cats.add(String(r.category));
    });
    return {
      cities: ["all", ...Array.from(cities).sort()],
      categories: ["all", ...Array.from(cats).sort()],
    };
  }, [rows]);

  /* ---- Filters + sort ---- */
  const filteredRows = useMemo(() => {
    let list = [...rows];
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter((r) =>
        [r?.name, r?.category, r?.venue?.city].filter(Boolean).some((v) => String(v).toLowerCase().includes(term))
      );
    }
    if (status !== "all") list = list.filter((r) => getStatusBadge(r).key === status);
    if (city !== "all") {
      list = list.filter((r) => String(r?.venue?.city || "").toLowerCase() === city.toLowerCase());
    }
    if (category !== "all") {
      list = list.filter((r) => String(r?.category || "").toLowerCase() === category.toLowerCase());
    }
    list.sort((a, b) => {
      const aName = (a?.name || "").toLowerCase();
      const bName = (b?.name || "").toLowerCase();
      const aDate = new Date(a?.date || a?.startDate || a?.start || a?.start_time || 0).getTime();
      const bDate = new Date(b?.date || b?.startDate || b?.start || b?.start_time || 0).getTime();
      switch (sort) {
        case "latest":
          return bDate - aDate;
        case "oldest":
          return aDate - bDate;
        case "az":
          return aName.localeCompare(bName);
        case "za":
          return bName.localeCompare(aName);
        default:
          return 0;
      }
    });
    return list;
  }, [rows, q, status, city, category, sort]);

  /* ---- Delete flow ---- */
  const onDelete = async (id) => {
    setConfirm({ open: false, id: null, name: "" });
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      await del(`${API.events.deleteEvent}/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });
      setRefreshTick((x) => x + 1);
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">🎭 Your Listed Shows</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">Create, edit, and manage your events effortlessly</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRefreshTick((x) => x + 1)}
            className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-amber-50 dark:hover:bg-amber-900/20"
          >
            Refresh
          </button>
          <Link
            to="/listyourshow/new"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
          >
            <span className="text-lg leading-none">＋</span>
            New Event
          </Link>
        </div>
      </div>

      {/* error banner */}
      {errorMsg && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm">{errorMsg}</div>
      )}

      {/* Filters — stack on mobile */}
      <div className="rounded-2xl border border-amber-100/70 dark:border-gray-800 bg-white/80 dark:bg-gray-900/60 backdrop-blur p-4 md:p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-5">
          <div className="md:col-span-2">
            <div className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title, city, or category…"
                className="w-full rounded-xl border px-4 py-2.5 pl-9 bg-white dark:bg-gray-950 dark:border-gray-800 text-sm"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-3.5-3.5" />
              </svg>
            </div>
          </div>

          {/* You can add more filter selects here (Status/City/Sort) if needed */}
          <Select
            label="Category"
            value={category}
            onChange={setCategory}
            options={options.categories.map((c) => ({ label: c === "all" ? "All" : c, value: c }))}
          />
          {/* Example (optional):
          <Select label="City" value={city} onChange={setCity}
            options={options.cities.map(c => ({ label: c === 'all' ? 'All' : c, value: c }))} />
          <Select label="Sort" value={sort} onChange={setSort}
            options={[{label:'Latest',value:'latest'},{label:'Oldest',value:'oldest'},{label:'A → Z',value:'az'},{label:'Z → A',value:'za'}]} />
          */}
        </div>
      </div>

      {/* Desktop TABLE (md and up) */}
      <div className="hidden md:block rounded-2xl border bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm overflow-hidden">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-amber-50/70 dark:bg-gray-800/80 backdrop-blur text-slate-600 dark:text-slate-300 uppercase text-xs">
              <tr>
                <Th>Event</Th>
                <Th>Category</Th>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="animate-pulse">
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-16 rounded-lg bg-slate-200 dark:bg-gray-800" />
                        <div>
                          <div className="h-4 w-40 rounded bg-slate-200 dark:bg-gray-800" />
                          <div className="mt-2 h-3 w-24 rounded bg-slate-200 dark:bg-gray-800" />
                        </div>
                      </div>
                    </Td>
                    <Td><div className="h-4 w-16 rounded bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td><div className="h-4 w-20 rounded bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td><div className="h-4 w-20 rounded bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td><div className="h-6 w-20 rounded-full bg-slate-200 dark:bg-gray-800" /></Td>
                    <Td className="text-right"><div className="h-8 w-24 rounded-full bg-slate-200 dark:bg-gray-800 ml-auto" /></Td>
                  </tr>
                ))}

              {!loading &&
                filteredRows.map((r) => {
                  const start = r?.date || r?.startDate || r?.start_time || r?.start;
                  const end = r?.endDate || r?.end_time || r?.end;
                  const badge = getStatusBadge(r);
                  const thumb = r?.imageUrl || r?.banner || r?.cover;

                  return (
                    <tr key={r._id || r.id} className="hover:bg-amber-50/40 dark:hover:bg-gray-800/40 transition-colors">
                      <Td>
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-16 rounded-lg overflow-hidden bg-slate-100 dark:bg-gray-800 border">
                            {thumb ? (
                              <img src={thumb} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full grid place-items-center text-slate-400">🎫</div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 dark:text-white truncate">{r?.name || "—"}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {r?.venue?.name ? `${r.venue.name} • ` : ""}
                              {r?.venue?.city || "—"}
                            </div>
                          </div>
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap">{r?.category || "—"}</Td>
                      <Td className="whitespace-nowrap">{formatDDMonYYYY(start)}</Td>
                      <Td className="whitespace-nowrap">{formatDDMonYYYY(end)}</Td>
                      <Td>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                            badge.cls
                          )}
                        >
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                          {badge.label}
                        </span>
                      </Td>
                      <Td className="text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => nav(`/listyourshow/view/${r._id || r.id}`)}
                            className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-amber-50 dark:hover:bg-gray-800"
                          >
                            View
                          </button>

                          <button
                            onClick={() => nav(`/events/dashboard/${r._id || r.id}`)}
                            className="rounded-full border border-blue-300 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            title="View Dashboard"
                          >
                            View Dashboard
                          </button>

                          {/* <button
                            onClick={() => setConfirm({ open: true, id: r._id || r.id, name: r?.name || "" })}
                            className="rounded-full border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                          >
                            Delete
                          </button> */}
                        </div>
                      </Td>
                    </tr>
                  );
                })}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <div className="text-5xl mb-2">🔍</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      No events match your filters. Try adjusting search or create a new one.
                    </div>
                    <Link
                      to="/admin/events/new"
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                    >
                      Create Event
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 text-xs text-slate-500 border-t dark:border-gray-800">
          Showing <span className="font-semibold">{filteredRows.length}</span> of{" "}
          <span className="font-semibold">{rows.length}</span> events
        </div>
      </div>

      {/* Mobile CARDS (below md) */}
      <div className="md:hidden space-y-3">
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={`skm-${i}`} className="rounded-2xl border bg-white dark:bg-gray-900 dark:border-gray-800 p-3 animate-pulse">
              <div className="flex gap-3">
                <div className="h-16 w-20 rounded-lg bg-slate-200 dark:bg-gray-800" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded bg-slate-200 dark:bg-gray-800" />
                  <div className="h-3 w-1/2 rounded bg-slate-200 dark:bg-gray-800" />
                  <div className="h-6 w-24 rounded-full bg-slate-200 dark:bg-gray-800" />
                </div>
              </div>
            </div>
          ))}

        {!loading && filteredRows.length === 0 && (
          <div className="rounded-2xl border bg-white dark:bg-gray-900 dark:border-gray-800 p-6 text-center">
            <div className="text-4xl mb-2">🔍</div>
            <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">No events found.</div>
            <Link
              to="/listyourshow/new"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
            >
              Create Event
            </Link>
          </div>
        )}

        {!loading &&
          filteredRows.map((r) => {
            const start = r?.date || r?.startDate || r?.start_time || r?.start;
            const end = r?.endDate || r?.end_time || r?.end;
            const badge = getStatusBadge(r);
            const thumb = r?.imageUrl || r?.banner || r?.cover;

            return (
              <div key={r._id || r.id} className="rounded-2xl border bg-white dark:bg-gray-900 dark:border-gray-800 p-3">
                <div className="flex gap-3">
                  <div className="h-20 w-24 rounded-lg overflow-hidden bg-slate-100 dark:bg-gray-800 border">
                    {thumb ? (
                      <img src={thumb} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full grid place-items-center text-slate-400">🎫</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-white truncate">{r?.name || "—"}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {r?.venue?.name ? `${r.venue.name} • ` : ""}
                      {r?.venue?.city || "—"}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                      <span>{r?.category || "—"}</span>
                      <span>•</span>
                      <span>{formatDDMonYYYY(start)} → {formatDDMonYYYY(end)}</span>
                    </div>
                    <div className="mt-2">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
                          badge.cls
                        )}
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                        {badge.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button
                    onClick={() => nav(`/listyourshow/view/${r._id || r.id}`)}
                    className="rounded-full border px-3 py-2 text-xs font-semibold hover:bg-amber-50 dark:hover:bg-gray-800"
                  >
                    View
                  </button>
                  <button
                    onClick={() => nav(`/events/dashboard/${r._id || r.id}`)}
                    className="rounded-full border border-blue-300 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => setConfirm({ open: true, id: r._id || r.id, name: r?.name || "" })}
                    className="rounded-full border border-rose-300 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* Confirm modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirm({ open: false, id: null, name: "" })} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-800 shadow-xl p-6">
            <div className="text-lg font-bold text-slate-900 dark:text-white">Delete Event</div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Are you sure you want to delete <span className="font-semibold">{confirm.name}</span>? This action cannot be undone.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirm({ open: false, id: null, name: "" })}
                className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-amber-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => onDelete(confirm.id)}
                className="rounded-full bg-rose-600 text-white px-4 py-2 text-sm font-semibold hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* tiny bits */
function Th({ children, className = "" }) {
  return <th className={cn("py-3 px-4 font-semibold", className)}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={cn("py-4 px-4 align-middle", className)}>{children}</td>;
}
function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">{label}</div>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-xl border px-3 py-2 bg-white dark:bg-gray-950 dark:border-gray-800 text-sm pr-8"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </label>
  );
}

function getStatusBadge(r) {
  if (r?.isActived === true) return { key: "active", cls: "bg-emerald-100 text-emerald-700", label: "Active" };
  if (r?.isActived === false) return { key: "inactive", cls: "bg-rose-100 text-rose-700", label: "Inactive" };
  if (r?.status === "published") return { key: "published", cls: "bg-emerald-100 text-emerald-700", label: "Published" };
  if (r?.status === "draft") return { key: "draft", cls: "bg-slate-200 text-slate-800", label: "Draft" };
  return { key: "unknown", cls: "bg-amber-100 text-amber-800", label: r?.status || "Unknown" };
}